import React, { useEffect, useState } from "react";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";

const formatExcelCellValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  // Normal cell values
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  // Excel date cells (display exact date/time as stored without timezone or locale conversion)
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return "";
    }
    const iso = value.toISOString();
    if (iso.endsWith("T00:00:00.000Z")) {
      return iso.slice(0, 10);
    }
    return iso.replace("T", " ").replace(/\.000Z$|Z$/, "");
  }

  // Rich-text cells
  if (Array.isArray(value?.richText)) {
    return value.richText.map((item) => item?.text ?? "").join("");
  }

  // Hyperlink cells
  if (typeof value?.text === "string") {
    return value.text;
  }

  // Formula cells
  if (
    typeof value === "object" &&
    Object.prototype.hasOwnProperty.call(value, "result")
  ) {
    return formatExcelCellValue(value.result);
  }

  // Excel error cells
  if (typeof value?.error === "string") {
    return value.error;
  }

  // Array values
  if (Array.isArray(value)) {
    return value
      .map(formatExcelCellValue)
      .filter((item) => item !== null && item !== "")
      .join(", ");
  }

  // Safely handle other unexpected objects
  return "";
};

const XlsxCsvViewer = ({ filePath }) => {
  const [sheetsData, setSheetsData] = useState({});
  const [cellStyles, setCellStyles] = useState({});
  const [activeSheet, setActiveSheet] = useState("");
  const [colWidths, setColWidths] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAndParseFile = async () => {
    try {
      const res = await fetch(filePath);
      if (!res.ok) throw new Error("Failed to fetch file");

      const arrayBuffer = await res.arrayBuffer();

      let sheetJsWb = null;
      try {
        sheetJsWb = XLSX.read(arrayBuffer, { type: "array" });
      } catch (e) {
        console.warn("SheetJS parse warning:", e);
      }

      let workbook = null;
      try {
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(arrayBuffer);
        workbook = wb;
      } catch (e) {
        workbook = null;
      }

      const parsedSheets = {};
      const parsedStyles = {};

      if (workbook) {
        workbook.eachSheet((sheet) => {
          const matrixData = [];
          const styleData = {};
          const sSheet = sheetJsWb?.Sheets?.[sheet.name];

          sheet.eachRow((row, rowNumber) => {
            const rowData = [];
            row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
              const sCell = sSheet ? sSheet[cell.address] : null;
              // sCell.w is the exact formatted text as displayed in Excel without conversion
              // sCell.v is the raw cell value
              const cellText = sCell ? (sCell.w !== undefined ? sCell.w : sCell.v) : undefined;
              if (cellText !== undefined && cellText !== null) {
                rowData.push(cellText);
              } else {
                rowData.push(formatExcelCellValue(cell.value));
              }

              // Existing background color logic remains unchanged.
              const fill = cell.fill;

              if (fill && fill.fgColor && fill.fgColor.argb) {
                styleData[`${rowNumber}-${colNumber}`] = {
                  bgColor: `#${fill.fgColor.argb.slice(2)}`,
                };
              }
            });
            matrixData.push(rowData);
          });

          parsedSheets[sheet.name] = matrixData;
          parsedStyles[sheet.name] = styleData;
        });
      } else if (sheetJsWb) {
        sheetJsWb.SheetNames.forEach((sheetName) => {
          const sSheet = sheetJsWb.Sheets[sheetName];
          parsedSheets[sheetName] = XLSX.utils.sheet_to_json(sSheet, {
            header: 1,
            raw: true,
            defval: null,
          });
        });
      }

      setSheetsData(parsedSheets);
      setCellStyles(parsedStyles);

      const sheetNames = Object.keys(parsedSheets);
      if (sheetNames.length > 0) {
        const defaultSheet = sheetNames[0];
        setActiveSheet(defaultSheet);
        const firstSheet = parsedSheets[defaultSheet] || [];
        const maxColumns = Math.max(0, ...firstSheet.map((row) => (row ? row.length : 0)));
        const widths = {};
        for (let i = 0; i < maxColumns; i++) widths[`Column ${i + 1}`] = 160;
        setColWidths(widths);
      }
    } catch (err) {
      console.error("Error parsing file:", err);
      setError("Failed to load and parse the file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filePath) fetchAndParseFile();
  }, [filePath]);

  const handleResize =
    (header) =>
    (event, { size }) => {
      setColWidths((prev) => ({ ...prev, [header]: size.width }));
    };

  const handleTabChange = (event, newValue) => {
    setActiveSheet(newValue);
    const sheetData = sheetsData[newValue];
    const maxColumns = Math.max(...sheetData.map((row) => row.length));
    const widths = {};
    for (let i = 0; i < maxColumns; i++)
      widths[`Column ${i + 1}`] = colWidths[`Column ${i + 1}`] || 160;
    setColWidths(widths);
  };

  const columnIndexToLetter = (index) => {
    let letters = "";
    while (index >= 0) {
      letters = String.fromCharCode((index % 26) + 65) + letters;
      index = Math.floor(index / 26) - 1;
    }
    return letters;
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );

  if (!Object.keys(sheetsData).length)
    return <Typography align="center">No data found</Typography>;

  const currentData = sheetsData[activeSheet] || [];
  const currentStyles = cellStyles[activeSheet] || {};
  const maxColumns = Math.max(...currentData.map((row) => row.length));
  const headers = Array.from({ length: maxColumns }, (_, i) =>
    columnIndexToLetter(i),
  );

  return (
    <Paper
      elevation={3}
      sx={{ display: "flex", flexDirection: "column", height: "82vh", p: 1 }}
    >
      <Typography variant="h6" mb={1}>
        File Preview
      </Typography>
      <Tabs
        value={activeSheet}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ style: { display: "none" } }}
      >
        {Object.keys(sheetsData).map((sheetName) => (
          <Tab
            key={sheetName}
            label={sheetName}
            value={sheetName}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: 2,
              mx: 1,
              backgroundColor:
                activeSheet === sheetName ? "lightblue" : "#e0e0e0",
              color: activeSheet === sheetName ? "#fff" : "#000",
              "&:hover": {
                backgroundColor:
                  activeSheet === sheetName ? "#6bb6ff" : "#d5d5d5",
              },
              minHeight: "36px",
              paddingX: 1,
              paddingY: 0.5,
            }}
          />
        ))}
      </Tabs>

      <Box sx={{ flex: 1, mt: 1, overflowX: "auto" }}>
        <Box
          sx={{
            minWidth: "100%",
            maxHeight: "100%",
            overflowY: "auto",
            borderTop: "1px solid #ddd",
            borderBottom: "1px solid #ddd",
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              borderCollapse: "collapse",
              width: "max-content",
              minWidth: "100%",
            }}
          >
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#e0e0e0",
                      border: "1px solid #ccc",
                      padding: 0,
                    }}
                  >
                    <ResizableBox
                      width={colWidths[header] || 160}
                      axis="x"
                      resizeHandles={["e"]}
                      onResize={handleResize(header)}
                      minConstraints={[80, 40]}
                      maxConstraints={[600, 300]}
                    >
                      <Box
                        sx={{
                          padding: "4px 8px",
                          fontWeight: "bold",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          minHeight: "48px",
                          textAlign: "center",
                          width: "100% !important",
                        }}
                      >
                        {header}
                      </Box>
                    </ResizableBox>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {currentData.map((row, r) => (
                <TableRow
                  key={r}
                  sx={{ backgroundColor: r % 2 === 0 ? "#fff" : "#f9f9f9" }}
                >
                  {headers.map((_, c) => {
                    const key = `${r + 1}-${c + 1}`;
                    const bgColor = currentStyles[key]?.bgColor || "inherit";
                    return (
                      <TableCell
                        key={c}
                        sx={{
                          border: "1px solid #eee",
                          padding: "4px 8px",
                          width: colWidths[String.fromCharCode(65 + c)] || 160,
                          maxWidth:
                            colWidths[String.fromCharCode(65 + c)] || 160,
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          backgroundColor: bgColor,
                        }}
                      >
                        {row[c] ?? "-"}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Paper>
  );
};

export default XlsxCsvViewer;
